import { OffsetRange } from '../core/offsetRange.js';
import { Range, Position } from './linesSliceCharSequence.js';

/**
 * A range of lines (1-based, end is exclusive).
 */
export class LineRange {
	constructor(
		public readonly startLineNumber: number,
		public readonly endLineNumberExclusive: number
	) {
		if (startLineNumber > endLineNumberExclusive) {
			throw new Error(`Invalid range: ${startLineNumber} > ${endLineNumberExclusive}`);
		}
	}

	get isEmpty(): boolean {
		return this.startLineNumber === this.endLineNumberExclusive;
	}

	get length(): number {
		return this.endLineNumberExclusive - this.startLineNumber;
	}

	join(other: LineRange): LineRange {
		return new LineRange(
			Math.min(this.startLineNumber, other.startLineNumber),
			Math.max(this.endLineNumberExclusive, other.endLineNumberExclusive)
		);
	}

	intersectsOrTouches(other: LineRange): boolean {
		return this.startLineNumber <= other.endLineNumberExclusive
			&& other.startLineNumber <= this.endLineNumberExclusive;
	}

	toOffsetRange(): OffsetRange {
		return new OffsetRange(this.startLineNumber - 1, this.endLineNumberExclusive - 1);
	}

	toString(): string {
		return `[${this.startLineNumber}, ${this.endLineNumberExclusive})`;
	}
}

/**
 * A mapping between two ranges.
 */
export class RangeMapping {
	constructor(
		public readonly originalRange: Range,
		public readonly modifiedRange: Range
	) {}

	toString(): string {
		return `${rangeToString(this.originalRange)} <-> ${rangeToString(this.modifiedRange)}`;
	}

	static assertSorted(rangeMappings: RangeMapping[]): void {
		for (let i = 1; i < rangeMappings.length; i++) {
			const prev = rangeMappings[i - 1];
			const cur = rangeMappings[i];
			if (!(prev.originalRange.endLineNumber <= cur.originalRange.startLineNumber ||
				(prev.originalRange.endLineNumber === cur.originalRange.startLineNumber && prev.originalRange.endColumn <= cur.originalRange.startColumn))) {
				throw new Error('RangeMappings must be sorted');
			}
		}
	}
}

function rangeToString(range: Range): string {
	return `(${range.startLineNumber}:${range.startColumn})-(${range.endLineNumber}:${range.endColumn})`;
}

/**
 * A mapping between two line ranges.
 */
export class LineRangeMapping {
	constructor(
		public readonly original: LineRange,
		public readonly modified: LineRange
	) {}

	toString(): string {
		return `${this.original} <-> ${this.modified}`;
	}

	/**
	 * This method assumes that the LineRangeMapping describes a valid diff!
	 * I.e. if one range is empty, the other range cannot be the entire document.
	 * It avoids various problems when the line range points to non-existing line-numbers.
	*/
	toRangeMapping2(originalLines: string[], modifiedLines: string[]): RangeMapping {
		const isValidLineNumber = (lineNumber: number, lines: string[]): boolean => {
			return lineNumber >= 1 && lineNumber <= lines.length;
		};

		const normalizePosition = (lineNumber: number, column: number, lines: string[]): Position => {
			if (lineNumber < 1) {
				return new Position(1, 1);
			}
			if (lineNumber > lines.length) {
				return new Position(lines.length, lines[lines.length - 1].length + 1);
			}
			const line = lines[lineNumber - 1];
			if (column > line.length + 1) {
				return new Position(lineNumber, line.length + 1);
			}
			return new Position(lineNumber, column);
		};

		if (isValidLineNumber(this.original.endLineNumberExclusive, originalLines)
			&& isValidLineNumber(this.modified.endLineNumberExclusive, modifiedLines)) {
			return new RangeMapping(
				new Range(this.original.startLineNumber, 1, this.original.endLineNumberExclusive, 1),
				new Range(this.modified.startLineNumber, 1, this.modified.endLineNumberExclusive, 1),
			);
		}

		if (!this.original.isEmpty && !this.modified.isEmpty) {
			return new RangeMapping(
				Range.fromPositions(
					new Position(this.original.startLineNumber, 1),
					normalizePosition(this.original.endLineNumberExclusive - 1, Number.MAX_SAFE_INTEGER, originalLines)
				),
				Range.fromPositions(
					new Position(this.modified.startLineNumber, 1),
					normalizePosition(this.modified.endLineNumberExclusive - 1, Number.MAX_SAFE_INTEGER, modifiedLines)
				),
			);
		}

		if (this.original.startLineNumber > 1 && this.modified.startLineNumber > 1) {
			return new RangeMapping(
				Range.fromPositions(
					normalizePosition(this.original.startLineNumber - 1, Number.MAX_SAFE_INTEGER, originalLines),
					normalizePosition(this.original.endLineNumberExclusive - 1, Number.MAX_SAFE_INTEGER, originalLines)
				),
				Range.fromPositions(
					normalizePosition(this.modified.startLineNumber - 1, Number.MAX_SAFE_INTEGER, modifiedLines),
					normalizePosition(this.modified.endLineNumberExclusive - 1, Number.MAX_SAFE_INTEGER, modifiedLines)
				),
			);
		}

		// Situation now: one range is empty and one range touches the last line and one range starts at line 1.
		// This shouldn't happen for valid diffs.
		throw new Error('Invalid diff: cannot convert to range mapping');
	}
}

/**
 * A detailed mapping between two line ranges, including inner changes.
 */
export class DetailedLineRangeMapping extends LineRangeMapping {
	constructor(
		original: LineRange,
		modified: LineRange,
		public readonly innerChanges: RangeMapping[] | undefined
	) {
		super(original, modified);
	}
}

/**
 * Result of a lines diff computation.
 */
export class LinesDiff {
	constructor(
		public readonly changes: DetailedLineRangeMapping[],
		public readonly moves: MovedText[],
		public readonly hitTimeout: boolean
	) {}
}

/**
 * Represents text that was moved from one location to another.
 */
export class MovedText {
	constructor(
		public readonly lineRangeMapping: LineRangeMapping,
		public readonly changes: DetailedLineRangeMapping[]
	) {}
}

/**
 * Helper to convert from range mappings to line range mappings.
 */
export function lineRangeMappingFromRangeMappings(
	alignments: RangeMapping[],
	originalLines: string[],
	modifiedLines: string[],
	dontAssertStartLine: boolean = false
): DetailedLineRangeMapping[] {
	const changes: DetailedLineRangeMapping[] = [];
	for (const g of groupAdjacentBy(
		alignments.map(a => getLineRangeMapping(a, originalLines, modifiedLines)),
		(a1, a2) =>
			a1.original.intersectsOrTouches(a2.original)
			|| a1.modified.intersectsOrTouches(a2.modified)
	)) {
		const first = g[0];
		const last = g[g.length - 1];

		changes.push(new DetailedLineRangeMapping(
			first.original.join(last.original),
			first.modified.join(last.modified),
			g.map(a => a.innerChanges![0])
		));
	}

	if (!dontAssertStartLine) {
		// Assert that the changes are sorted and non-overlapping (but don't throw - just log in debug)
		for (let i = 1; i < changes.length; i++) {
			const prev = changes[i - 1];
			const cur = changes[i];
			if (prev.original.endLineNumberExclusive > cur.original.startLineNumber ||
				prev.modified.endLineNumberExclusive > cur.modified.startLineNumber) {
				// Silently skip validation issue - may be expected in some edge cases
				break;
			}
		}
	}

	return changes;
}

function getLineRangeMapping(rangeMapping: RangeMapping, originalLines: string[], modifiedLines: string[]): DetailedLineRangeMapping {
	let lineStartDelta = 0;
	let lineEndDelta = 0;

	// rangeMapping describes the edit that replaces `rangeMapping.originalRange` with `newText := getText(modifiedLines, rangeMapping.modifiedRange)`.

	// original: ]xxx \n <- this line is not modified
	// modified: ]xx  \n
	if (rangeMapping.modifiedRange.endColumn === 1 && rangeMapping.originalRange.endColumn === 1
		&& rangeMapping.originalRange.startLineNumber + lineStartDelta <= rangeMapping.originalRange.endLineNumber
		&& rangeMapping.modifiedRange.startLineNumber + lineStartDelta <= rangeMapping.modifiedRange.endLineNumber) {
		// We can only do this if the range is not empty yet
		lineEndDelta = -1;
	}

	// original: xxx[ \n <- this line is not modified
	// modified: xxx[ \n
	if (rangeMapping.modifiedRange.startColumn - 1 >= (modifiedLines[rangeMapping.modifiedRange.startLineNumber - 1]?.length ?? 0)
		&& rangeMapping.originalRange.startColumn - 1 >= (originalLines[rangeMapping.originalRange.startLineNumber - 1]?.length ?? 0)
		&& rangeMapping.originalRange.startLineNumber <= rangeMapping.originalRange.endLineNumber + lineEndDelta
		&& rangeMapping.modifiedRange.startLineNumber <= rangeMapping.modifiedRange.endLineNumber + lineEndDelta) {
		// We can only do this if the range is not empty yet
		lineStartDelta = 1;
	}

	const originalLineRange = new LineRange(
		rangeMapping.originalRange.startLineNumber + lineStartDelta,
		rangeMapping.originalRange.endLineNumber + 1 + lineEndDelta
	);
	const modifiedLineRange = new LineRange(
		rangeMapping.modifiedRange.startLineNumber + lineStartDelta,
		rangeMapping.modifiedRange.endLineNumber + 1 + lineEndDelta
	);

	return new DetailedLineRangeMapping(originalLineRange, modifiedLineRange, [rangeMapping]);
}

function* groupAdjacentBy<T>(items: Iterable<T>, shouldBeGrouped: (item1: T, item2: T) => boolean): Iterable<T[]> {
	let currentGroup: T[] | undefined;
	let last: T | undefined;
	for (const item of items) {
		if (last !== undefined && shouldBeGrouped(last, item)) {
			currentGroup!.push(item);
		} else {
			if (currentGroup) {
				yield currentGroup;
			}
			currentGroup = [item];
		}
		last = item;
	}
	if (currentGroup) {
		yield currentGroup;
	}
}

/**
 * Interface for text that provides lines.
 */
export interface IText {
	readonly lineCount: number;
	getLineContent(lineNumber: number): string;
}

export class ArrayText implements IText {
	constructor(public readonly lines: string[]) {}

	get lineCount(): number {
		return this.lines.length;
	}

	getLineContent(lineNumber: number): string {
		return this.lines[lineNumber - 1];
	}
}
